import { getQueryClient } from 'app/get-query-client';
import { useAuthStore } from 'lib/stores/authStore';
import type { ServerError } from 'models';
import type { ReferralOfferResult, RewardCodeType } from 'models/reqres';
import { getCountryCodeSnapshot } from 'queries/countryCode';
import { referralsSummaryKeys } from 'queries/referralsSummary';
import { rewardsHubKeys } from 'queries/rewardsHubKeys';
import { getReferralOffer, redeemPromoCode } from 'transport/domains/referrals';
import { postSignupReferralCode } from 'transport/domains/signup';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { getReferralSignupParam } from 'utils/referralAttribution';
import { fetchSardineSessionKey } from 'utils/sardine';

const REFERRAL_INFO_REFRESH_DELAY_MS = 1000;

function refreshReferralInfo(): void {
    void getQueryClient().invalidateQueries({ queryKey: referralsSummaryKeys.all });
    void getQueryClient().invalidateQueries({ queryKey: rewardsHubKeys.all });
}

export type RewardCodeErrorKind = 'invalid' | 'ineligible' | 'already_applied' | 'unknown';

export class RewardCodeApplicationError extends Error {
    constructor(
        public readonly kind: RewardCodeErrorKind,
        public readonly type?: RewardCodeType,
    ) {
        super(rewardCodeErrorMessage(kind));
    }
}

export class RewardCodeApplicationCancelled extends Error {}

export const REWARD_CODE_SUCCESS_MESSAGE = 'Reward applied!';

export function rewardCodeErrorMessage(kind: RewardCodeErrorKind): string {
    switch (kind) {
        case 'invalid':
            return 'The code is invalid or expired';
        case 'ineligible':
            return "This code isn't available to you";
        case 'already_applied':
            return 'Code already applied';
        case 'unknown':
            return 'Something went wrong. Please try again.';
    }
}

export function normalizeRewardCodeError(error: ServerError, type?: RewardCodeType): RewardCodeApplicationError {
    switch (error.error.code.toLowerCase()) {
        case 'user_referral_blocked':
        case 'promo_code_invalid':
            return new RewardCodeApplicationError('invalid', type);
        case 'user_not_referral_eligible':
        case 'permission_denied':
        case 'promo_code_user_ineligible':
            return new RewardCodeApplicationError('ineligible', type);
        case 'promo_code_already_applied':
            return new RewardCodeApplicationError('already_applied', type);
        default:
            return new RewardCodeApplicationError('unknown', type);
    }
}

const EXPECTED_REWARD_CODE_ERROR_CODES = new Set([
    'user_referral_blocked',
    'user_not_referral_eligible',
    'permission_denied',
    'promo_code_invalid',
    'promo_code_user_ineligible',
    'promo_code_already_applied',
]);

function logRewardCodeTransportError(error: ServerError): void {
    if (EXPECTED_REWARD_CODE_ERROR_CODES.has(error.error.code.toLowerCase())) return;
    logProviderFactory.logError(`Reward code application failed: ${error.error.code}`);
}

function previewRewardCode(code: string): Promise<ReferralOfferResult> {
    return new Promise((resolve, reject) => {
        getReferralOffer(
            code,
            resolve,
            () => reject(new RewardCodeApplicationError('unknown')),
            () => reject(new RewardCodeApplicationCancelled()),
        );
    });
}

function redeemPromo(code: string): Promise<void> {
    return new Promise((resolve, reject) => {
        redeemPromoCode(
            code,
            resolve,
            (error) => reject(normalizeRewardCodeError(error, 'promo_code')),
            () => reject(new RewardCodeApplicationCancelled()),
            logRewardCodeTransportError,
        );
    });
}

function attachReferral(
    email: string,
    code: string,
    isCurrent: () => boolean,
    isEligible: () => boolean,
): Promise<void> {
    return new Promise((resolve, reject) => {
        void submitPostSignupReferralCode(
            email,
            code,
            resolve,
            (error) => reject(normalizeRewardCodeError(error, 'referral')),
            () => reject(new RewardCodeApplicationCancelled()),
            logRewardCodeTransportError,
            isCurrent,
            isEligible,
        ).then((outcome) => {
            if (outcome === 'ineligible') reject(new RewardCodeApplicationError('ineligible', 'referral'));
            if (outcome === 'session-ended' || outcome === 'cancelled') reject(new RewardCodeApplicationCancelled());
        });
    });
}

export async function applyRewardCode(
    email: string,
    rawCode: string,
    isCurrent: () => boolean = () => true,
): Promise<RewardCodeType> {
    const code = rawCode.trim();
    if (!code) throw new RewardCodeApplicationError('invalid');
    if (!isCurrent()) throw new RewardCodeApplicationCancelled();

    const offer = await previewRewardCode(code);
    if (!isCurrent()) throw new RewardCodeApplicationCancelled();
    const type = offer.type ?? 'referral';

    if (type === 'promo_code') {
        await redeemPromo(code);
    } else {
        if (offer.valid === false) throw new RewardCodeApplicationError('invalid', type);
        if (offer.valid !== true) throw new RewardCodeApplicationError('unknown', type);
        const isEligible = (): boolean => {
            const country = useAuthStore.getState().user?.country || getCountryCodeSnapshot();
            return !!getReferralSignupParam(code, country);
        };
        if (!isEligible()) throw new RewardCodeApplicationError('ineligible', type);
        await attachReferral(email, code, isCurrent, isEligible);
    }

    if (!isCurrent()) throw new RewardCodeApplicationCancelled();
    refreshReferralInfo();
    return type;
}

/**
 * Whether the request was actually issued. Callers that bridge these callbacks into a
 * promise must handle `'session-ended'` explicitly: neither `onSuc` nor `onErr` fires
 * in that case, so a bridge that settles only from the callbacks would hang forever
 * (see `runReferralAttachForNewApplicant`, whose stranded promise also stranded its
 * `activeReferralAttaches` entry and blocked any later retry for that user).
 */
export type PostSignupReferralOutcome = 'submitted' | 'session-ended' | 'cancelled' | 'ineligible';

export async function submitPostSignupReferralCode(
    email: string,
    referralCode: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = () => undefined,
    onLog?: (err: ServerError) => void,
    isCurrent: () => boolean = () => true,
    isEligible: () => boolean = () => true,
): Promise<PostSignupReferralOutcome> {
    const sardine = await fetchSardineSessionKey('signup');
    // Session ended mid-fetch; the referral belongs to the signup that just ended.
    if (!sardine.ok) return 'session-ended';
    if (!isCurrent()) return 'cancelled';
    if (!isEligible()) return 'ineligible';
    const sardineSessionKey = sardine.key;
    postSignupReferralCode(
        {
            email,
            referral_code: referralCode,
            sardine_session_key: sardineSessionKey || undefined,
        },
        () => {
            setTimeout(refreshReferralInfo, REFERRAL_INFO_REFRESH_DELAY_MS);
            onSuc();
        },
        onErr,
        onFinally,
        onLog,
    );
    return 'submitted';
}
